import { TFunction } from 'i18next';
import { AnomalyDirection, AnomalyIncident } from '../../../../../../../shared/models/anomaly-detection/models/AnomalyIncident';
import { AnomalyMetricKey } from '../../../../../../../shared/models/anomaly-detection/models/AnomalyIncidentsResponse';
import { GroupedAnomalyMarker } from '../../groupedAnomalyMarkers';

export type MarkerMetricIncident = {
  incident: AnomalyIncident;
  metric: AnomalyMetricKey;
  unit: string;
};

type AnomalyMetricMarkerField = Exclude<keyof GroupedAnomalyMarker, 'dateKey'>;

type AnomalyMetricDefinition = {
  key: AnomalyMetricKey;
  markerField: AnomalyMetricMarkerField;
  valueUnit: string;
  nameKey: string;
  improvedKey: string;
  deterioratedKey: string;
};

/** Ordered registry of chart anomaly metrics — add new metrics here. */
export const ANOMALY_METRIC_DEFINITIONS: AnomalyMetricDefinition[] = [
  {
    key: 'brand_score',
    markerField: 'brandScore',
    valueUnit: '%',
    nameKey: 'brandScore',
    improvedKey: 'brandScoreImproved',
    deterioratedKey: 'brandScoreDeteriorated',
  },
  {
    key: 'average_position',
    markerField: 'averagePosition',
    valueUnit: '',
    nameKey: 'averagePosition',
    improvedKey: 'averagePositionImproved',
    deterioratedKey: 'averagePositionDeteriorated',
  },
  {
    key: 'visibility',
    markerField: 'visibility',
    valueUnit: '%',
    nameKey: 'visibility',
    improvedKey: 'visibilityImproved',
    deterioratedKey: 'visibilityDeteriorated',
  },
];

export const ANOMALY_METRIC_ORDER: AnomalyMetricKey[] = ANOMALY_METRIC_DEFINITIONS.map(
  (definition) => definition.key,
);

export const compareAnomalyMetrics = (left: AnomalyMetricKey, right: AnomalyMetricKey): number =>
  ANOMALY_METRIC_ORDER.indexOf(left) - ANOMALY_METRIC_ORDER.indexOf(right);

const getAnomalyMetricDefinition = (metric: AnomalyMetricKey): AnomalyMetricDefinition | undefined =>
  ANOMALY_METRIC_DEFINITIONS.find((definition) => definition.key === metric);

export const collectMarkerMetricIncidents = (marker: GroupedAnomalyMarker): MarkerMetricIncident[] => {
  const incidents: MarkerMetricIncident[] = [];

  for (const definition of ANOMALY_METRIC_DEFINITIONS) {
    const incident = marker[definition.markerField];
    if (incident) {
      incidents.push({
        incident,
        metric: definition.key,
        unit: definition.valueUnit,
      });
    }
  }

  return incidents;
};

export const getAnomalyMetricLabel = (t: TFunction, metric: AnomalyMetricKey): string => {
  const definition = getAnomalyMetricDefinition(metric);
  return definition ? t(definition.nameKey) : metric;
};

export const getAnomalyMetricVerb = (
  t: TFunction,
  metric: AnomalyMetricKey,
  direction: AnomalyDirection,
): string => {
  const definition = getAnomalyMetricDefinition(metric);
  if (!definition) {
    return '';
  }

  return t(direction === 'positive' ? definition.improvedKey : definition.deterioratedKey);
};

export const buildAnomalyMetricLabels = (t: TFunction): Record<AnomalyMetricKey, string> => {
  const labels = {} as Record<AnomalyMetricKey, string>;

  for (const definition of ANOMALY_METRIC_DEFINITIONS) {
    labels[definition.key] = t(definition.nameKey);
  }

  return labels;
};
